// Pure HDF5 grid + component loaders. No THREE, no DOM.
//
// The progress callback (if supplied) receives a number in [0,1] as bytes stream.

import * as hdf5 from 'jsfive';
import type {
  FetchProgressOpts,
  FieldComponentsResult,
  FieldMetadata,
} from './types';

type AttrMap = hdf5.Hdf5Attrs;
type Node = hdf5.Hdf5Dataset | hdf5.Hdf5Group | null | undefined;

function isDataset(node: Node): node is hdf5.Hdf5Dataset {
  return !!node && 'value' in node && 'shape' in node;
}

function attrString(attrs: AttrMap, key: string): string | null {
  const v = attrs[key];
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return null;
}

function attrNumber(attrs: AttrMap, key: string): number | null {
  const v = attrs[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
}

function firstNum(value: hdf5.Hdf5Value, index: number, fallback: number): number {
  const v = (value as ArrayLike<number>)[index];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export async function fetchFieldMetadata(url: string, { onProgress }: FetchProgressOpts = {}): Promise<FieldMetadata | null> {
  try {
    const { buffer } = await fetchWithProgress(url, onProgress);
    const file = new hdf5.File(buffer, url);
    const attrs: AttrMap = file.attrs ?? {};
    const X_UNITS = attrString(attrs, 'x_units');
    const x = file.get('x'), y = file.get('y'), z = file.get('z');
    if (!isDataset(x) || !isDataset(y) || !isDataset(z)) return null;
    const xv = x.value, yv = y.value, zv = z.value;
    const xLen = (xv as ArrayLike<number>).length;
    const yLen = (yv as ArrayLike<number>).length;
    const zLen = (zv as ArrayLike<number>).length;
    const gxMin = attrNumber(attrs, 'x_min') ?? firstNum(xv, 0, 0);
    const gxMax = attrNumber(attrs, 'x_max') ?? firstNum(xv, xLen - 1, 0);
    const gyMin = attrNumber(attrs, 'y_min') ?? firstNum(yv, 0, 0);
    const gyMax = attrNumber(attrs, 'y_max') ?? firstNum(yv, yLen - 1, 0);
    const gzMin = attrNumber(attrs, 'z_min') ?? firstNum(zv, 0, 0);
    const gzMax = attrNumber(attrs, 'z_max') ?? firstNum(zv, zLen - 1, 0);
    const dx = xLen > 1 ? firstNum(xv, 1, 1) - firstNum(xv, 0, 0) : 1;
    const dy = yLen > 1 ? firstNum(yv, 1, 1) - firstNum(yv, 0, 0) : 1;
    const dz = zLen > 1 ? firstNum(zv, 1, 1) - firstNum(zv, 0, 0) : 1;
    const candidates = ['Bx', 'By', 'Bz'] as const;
    const field_components: string[] = candidates.filter((n) => isDataset(file.get(n)));
    const firstName = field_components[0];
    const firstNode = firstName ? file.get(firstName) : null;
    const first = isDataset(firstNode) ? firstNode : null;
    const field_units = first ? (attrString(first.attrs, 'units') ?? 'code units') : 'code units';
    return {
      coordinate_system: attrString(attrs, 'coordinate_system') ?? 'GSM',
      units: X_UNITS ?? attrString(attrs, 'units') ?? 'RE',
      grid_bounds: {
        x: { min: gxMin, max: gxMax },
        y: { min: gyMin, max: gyMax },
        z: { min: gzMin, max: gzMax },
      },
      resolution: { x: dx, y: dy, z: dz },
      dims: { nx: xLen, ny: yLen, nz: zLen },
      field_components,
      field_units,
      field_name_short: attrString(attrs, 'field_name_short'),
      field_name_long: attrString(attrs, 'field_name_long'),
      title: attrString(attrs, 'title'),
      model_name: attrString(attrs, 'model_name'),
      time: attrNumber(attrs, 'time'),
      time_units: attrString(attrs, 'time_units'),
      doi: attrString(attrs, 'doi'),
      summary: attrString(attrs, 'summary'),
    };
  } catch {
    return null;
  }
}

export async function fetchFieldComponents(
  url: string,
  components: string[],
  { onProgress }: FetchProgressOpts = {},
): Promise<FieldComponentsResult | null> {
  try {
    if (!Array.isArray(components) || components.length === 0) return null;
    const { buffer } = await fetchWithProgress(url, onProgress);
    const file = new hdf5.File(buffer, url);
    const firstName = components[0]!;
    const firstNode = file.get(firstName);
    if (!isDataset(firstNode)) return null;
    let nx = 0, ny = 0, nz = 0;
    if (firstNode.shape && firstNode.shape.length === 3) {
      nx = firstNode.shape[0]!; ny = firstNode.shape[1]!; nz = firstNode.shape[2]!;
    }
    const total = nx * ny * nz;
    const componentsData: Record<string, Float32Array> = {};
    for (const name of components) {
      const ds = file.get(name);
      if (!isDataset(ds)) return null;
      const val = ds.value;
      if (!isFlatNumeric(val) || (val as ArrayLike<number>).length !== total) return null;
      componentsData[name] = toFloat32(val);
    }
    // Yield one task tick so the 100% progress dispatch queued by
    // fetchWithProgress can paint before this CPU-bound magnitude pass
    // freezes the main thread. Couples a UI concern into pure physics; the
    // clean fix is to split fetch from parse/compute and let app/fieldData
    // insert the yield. Until then, this one line is the perceptual fix.
    await new Promise((resolve) => setTimeout(resolve, 0));
    const magnitude = new Float32Array(total);
    let minVal = Infinity, maxVal = -Infinity;
    let p = 0;
    for (let i = 0; i < nx; i++) {
      for (let j = 0; j < ny; j++) {
        for (let k = 0; k < nz; k++) {
          const idx3 = i * (ny * nz) + j * nz + k;
          let sumSq = 0;
          for (const name of components) {
            const v = componentsData[name]![idx3]!;
            sumSq += v * v;
          }
          const mag = Math.sqrt(sumSq);
          magnitude[p++] = mag;
          if (mag < minVal) minVal = mag;
          if (mag > maxVal) maxVal = mag;
        }
      }
    }
    return { nx, ny, nz, componentsData, magnitude, minVal, maxVal };
  } catch {
    return null;
  }
}

function isFlatNumeric(v: hdf5.Hdf5Value): boolean {
  return ArrayBuffer.isView(v) || Array.isArray(v);
}

function toFloat32(v: hdf5.Hdf5Value): Float32Array {
  if (v instanceof Float32Array) return v;
  if (v instanceof BigInt64Array || v instanceof BigUint64Array) {
    const out = new Float32Array(v.length);
    for (let i = 0; i < v.length; i++) out[i] = Number(v[i]);
    return out;
  }
  return Float32Array.from(v as ArrayLike<number>);
}

interface ProgressBuffer {
  buffer: ArrayBuffer;
}

async function fetchWithProgress(url: string, onProgress?: (fraction: number) => void): Promise<ProgressBuffer> {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) return { buffer: await response.arrayBuffer() };
  // Only take a reader once we know we will stream: getReader() locks the body, and a locked
  // body makes the arrayBuffer() fallback throw. Servers that gzip without a Content-Length
  // (vite preview, nginx with gzip on, many CDNs) land here, so getting this wrong fails the
  // dataset load outright rather than merely losing the progress bar.
  const total = Number(response.headers.get('Content-Length')) || 0;
  if (!total || !response.body?.getReader) return { buffer: await response.arrayBuffer() };
  const reader = response.body.getReader();
  let received = 0;
  const chunks: Uint8Array[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.byteLength;
      if (onProgress) onProgress(received / total);
    }
  }
  const buffer = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) { buffer.set(chunk, offset); offset += chunk.byteLength; }
  return { buffer: buffer.buffer };
}
